import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { EnvironmentService } from '../services/environment/environment.service';
import { UserService } from '../services/user/user.service';

export const authInterceptor: HttpInterceptorFn = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {

    // Get the services we need
    const environmentService = inject(EnvironmentService);
    const userService = inject(UserService);




    // Get environment values directly
    const { adminClientId, adminClientSecret, adminSalesChannel } = environment;

    // Check if this is an admin API call
    if (req.url.includes('admin/sales_channel_index')) {


        const modifiedReq = req.clone({
            headers: req.headers
                .set('Content-Type', 'application/json')
                .set('clientid', adminClientId)
                .set('clientsecret', adminClientSecret)
                .set('saleschannel', adminSalesChannel)
        });
        return next(modifiedReq);
    }

    // For warranty-generate-presigned-url api call setting the header
    if (req.url.includes('warranty-generate-presigned-url')) {
        const SERVICE_AUTH_ID = 'b73c4431-b2de-434f-91f5-1c5042c439b3';
        const modifiedReq = req.clone({
            headers: req.headers
                .set('Content-Type', 'application/json')
                .set('SERVICE-AUTH-ID', SERVICE_AUTH_ID)
        });
        return next(modifiedReq);
    }

    // Handle no-auth header requests (like PDF uploads)
    if (req.headers.has('no-auth')) {

        const newHeaders = req.headers.delete('no-auth');
        const modifiedReq = req.clone({ headers: newHeaders.set('Content-Type', 'application/pdf') });
        return next(modifiedReq);
    }

    // For ALL APIs, check user state and apply appropriate headers
    const isAuthenticated = userService.isAuthenticated();
    const user = userService.user();

    let modifiedReq: HttpRequest<any> | undefined;

    // CASE 1: User EXISTS and is AUTHENTICATED - send client credentials, sales channel, AND Authorization
    if (isAuthenticated && user && Object.keys(user).length > 0) {
        try {
            // Try to get dynamic environment values first
            const env = environmentService.environment;
            const envValues = env?.();
            if (envValues?.client_id && envValues?.client_secret && envValues?.sales_channel_code) {
                modifiedReq = req.clone({
                    headers: req.headers
                        .set('Content-Type', 'application/json')
                        .set('clientid', envValues.client_id)
                        .set('clientsecret', envValues.client_secret)
                        .set('saleschannel', envValues.sales_channel_code)
                        .set('Authorization', `Bearer ${user.access_token}`)
                });

                return next(modifiedReq);
            }
        } catch (error) {
            // Environment not initialized yet, fallback to admin credentials
        }

        // Fallback to admin credentials
        if (adminClientId && adminClientSecret && adminSalesChannel) {
            modifiedReq = req.clone({
                headers: req.headers
                    .set('Content-Type', 'application/json')
                    .set('clientid', adminClientId)
                    .set('clientsecret', adminClientSecret)
                    .set('saleschannel', adminSalesChannel)
                    .set('Authorization', `Bearer ${user.access_token}`)
            });

            return next(modifiedReq);
        }
    }

    // CASE 2: NO User or NOT AUTHENTICATED - send client credentials WITHOUT Authorization header
    if (!isAuthenticated || !user || Object.keys(user).length === 0) {
        try {
            // Try to get dynamic environment values first
            const env = environmentService.environment;
            const envValues = env?.();
            if (envValues?.client_id && envValues?.client_secret && envValues?.sales_channel_code) {
                modifiedReq = req.clone({
                    headers: req.headers
                        .set('Content-Type', 'application/json')
                        .set('clientid', envValues.client_id)
                        .set('clientsecret', envValues.client_secret)
                        .set('saleschannel', envValues.sales_channel_code)
                });


                return next(modifiedReq);
            }
        } catch (error) {
            // Environment not initialized yet, fallback to admin credentials
        }

        // Fallback to admin credentials for unauthenticated requests
        if (adminClientId && adminClientSecret && adminSalesChannel) {
            modifiedReq = req.clone({
                headers: req.headers
                    .set('Content-Type', 'application/json')
                    .set('clientid', adminClientId)
                    .set('clientsecret', adminClientSecret)
                    .set('saleschannel', adminSalesChannel)
            });


            return next(modifiedReq);
        }

        // Final fallback: no Authorization header for unauthenticated requests
        return next(req);
    }

    // Fallback: no modifications
    return next(req);
};
